import React, { useState, useCallback, PropsWithChildren } from 'react';
import { useSelector, useStore } from 'react-redux';

import StorageUsageMonitorContext from 'app/providers/StorageUsageMonitorProvider/context';
import StorageUsageMonitor from 'app/adapters/StorageUsageMonitor';
import { selectDeviceIdentifier } from 'app/redux/deviceParameters/reducer';
import os from 'app/native/node/os';
import getElectron from 'app/native/node/electron';
import { Button, Checkbox, Icon, Modal } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import { selectStorage, setAutomaticCleanupMode } from 'app/redux/storage/reducer';

// eslint-disable-next-line react/prop-types
function StorageUsageMonitorProviderImpl({ children }: PropsWithChildren<{}>) {
  const store = useStore();

  const [deviceName] = useState(() => os()?.hostname());
  const deviceIdentifier = useSelector(selectDeviceIdentifier);
  const automaticCleanupMode = useSelector(selectStorage).automaticCleanupMode;
  const [isStorageWarningDisplayed, setIsStorageWarningDisplayed] = useState(false);
  const showStorageWarning = useCallback(
    () => setIsStorageWarningDisplayed(true),
    [setIsStorageWarningDisplayed]
  );

  const [storageUsageMonitor] = useState(
    () => new StorageUsageMonitor(deviceName, deviceIdentifier, store, showStorageWarning)
  );
  storageUsageMonitor.deviceName = deviceName;
  storageUsageMonitor.deviceIdentifier = deviceIdentifier;
  storageUsageMonitor.store = store;
  storageUsageMonitor.showStorageWarning = showStorageWarning;

  return (
    <>
      <StorageUsageMonitorContext.Provider value={storageUsageMonitor}>
        {children}
      </StorageUsageMonitorContext.Provider>
      <Modal
        open={isStorageWarningDisplayed}
        onOpen={() => setIsStorageWarningDisplayed(true)}
        onClose={() => setIsStorageWarningDisplayed(false)}
        closeIcon
      >
        <Modal.Header className="flex center">
          <Icon name="save" size="large" />
          <FormattedMessage id="storage.warning.title" />
        </Modal.Header>
        <Modal.Content>
          {/* @ts-ignore */}
          <pre style={{ textWrap: 'auto' }}>
            <FormattedMessage id="storage.warning.description" />
          </pre>
          <br />
          <div>
            <Checkbox
              checked={automaticCleanupMode === 'pacs'}
              onChange={(e, { checked }) =>
                store.dispatch(setAutomaticCleanupMode(checked ? 'pacs' : 'none'))
              }
              label={
                <label>
                  <b>
                    <FormattedMessage id="storage.enableDeletePACSOnly.label" />
                  </b>
                </label>
              }
            />
            <Checkbox
              checked={automaticCleanupMode === 'all'}
              onChange={(e, { checked }) =>
                store.dispatch(setAutomaticCleanupMode(checked ? 'all' : 'none'))
              }
              label={
                <label>
                  <b>
                    <FormattedMessage id="storage.enableDeleteAll.label" />
                  </b>
                </label>
              }
            />
          </div>
        </Modal.Content>
        <Modal.Actions className="flex center">
          <Button onClick={() => setIsStorageWarningDisplayed(false)} secondary>
            <FormattedMessage id="storage.warning.closeButton" />
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}

// eslint-disable-next-line react/prop-types
export default function StorageUsageMonitorProvider({ children }: PropsWithChildren<{}>) {
  if (!getElectron()) return children;
  return <StorageUsageMonitorProviderImpl>{children}</StorageUsageMonitorProviderImpl>;
}
